import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, montant } from '../lib/format';
import type { Vehicule } from '../api/types';

interface Calcul { partLocataireMinor: number; regleAppliquee: string; detailCalcul: string; base: { reparation: number; mainOeuvre: number; immobilisation: number } }
interface Sinistre {
  _id: string; date: string; partLocataireMinor: number; detailCalcul: string;
  cautionDeduiteMinor: number; resteADevoirMinor: number; reglee: boolean;
  circonstances: { ivresse: boolean; faitVolontaire: boolean; negligence: boolean; locataireFautif: boolean };
  vehicle?: { marque: string; modele: string; immatriculation: string };
  reservation?: { numero: string };
}

/** L'article 06, rendu automatique — et sa regle ecrite en toutes lettres. */
export default function Sinistres() {
  const { peut } = useAuth();
  const [liste, setListe] = useState<Sinistre[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [calcul, setCalcul] = useState<Calcul | null>(null);
  const [erreur, setErreur] = useState('');
  const [msg, setMsg] = useState('');

  const [f, setF] = useState({
    vehicle: '', ivresse: false, faitVolontaire: false, negligence: false, locataireFautif: false,
    carrosserie: '', mecanique: '', mainOeuvre: '', jours: '', tarifJour: '', assurance: '',
  });

  useEffect(() => {
    api.get<Sinistre[]>('/operations/sinistres').then(setListe);
    api.get<Vehicule[]>('/vehicules').then(setVehicules);
  }, []);

  const entree = () => ({
    circonstances: { ivresse: f.ivresse, faitVolontaire: f.faitVolontaire, negligence: f.negligence, locataireFautif: f.locataireFautif },
    devisCarrosserieMinor: centimes(Number(f.carrosserie || 0)),
    devisMecaniqueMinor: centimes(Number(f.mecanique || 0)),
    mainOeuvreMinor: centimes(Number(f.mainOeuvre || 0)),
    joursImmobilisation: Number(f.jours || 0),
    tarifJourMinor: centimes(Number(f.tarifJour || 0)),
    priseEnChargeAssuranceMinor: centimes(Number(f.assurance || 0)),
  });

  useEffect(() => {
    const t = setTimeout(() => {
      api.post<Calcul>('/operations/sinistres/simuler', entree()).then(setCalcul).catch(() => setCalcul(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  if (!liste) return <Squelette lignes={6} />;

  async function enregistrer() {
    setErreur('');
    try {
      await api.post('/operations/sinistres', { ...entree(), vehicle: f.vehicle });
      setMsg('Sinistre enregistré.');
      api.get<Sinistre[]>('/operations/sinistres').then(setListe);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const bascule = (k: keyof typeof f) => setF({ ...f, [k]: !f[k] } as typeof f);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Accidents</h1>
          <p>Calculateur de l'article 06 — la règle appliquée est écrite en toutes lettres</p>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <Carte titre="Circonstances et montants">
          <Champ label="Véhicule">
            <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value, tarifJour: String((vehicules.find((v) => v._id === e.target.value)?.tarifHauteSaisonMinor ?? 0) / 100) })}>
              <option value="">Choisir…</option>
              {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
            </select>
          </Champ>

          <div style={{ marginTop: 14 }}>
            <div className="lbl" style={{ marginBottom: 6 }}>Circonstances</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {([
                ['ivresse', "Conduite en état d'ivresse"],
                ['faitVolontaire', 'Fait volontaire'],
                ['negligence', 'Négligence'],
                ['locataireFautif', 'Locataire fautif à 100 %'],
              ] as const).map(([k, l]) => (
                <label key={k} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '8px 10px', border: '1px solid var(--bord)', borderRadius: 'var(--r-champ)', cursor: 'pointer', background: f[k] ? 'var(--danger-doux)' : 'var(--surface)' }}>
                  <input type="checkbox" checked={f[k]} onChange={() => bascule(k)} />{l}
                </label>
              ))}
            </div>
            <p className="mini sourdine" style={{ marginTop: 6 }}>
              Aucune case cochée = locataire non fautif : la règle des 60 % s'applique.
            </p>
          </div>

          <div className="grille g2" style={{ marginTop: 14 }}>
            <Champ label="Devis carrosserie (DZD)"><input type="number" value={f.carrosserie} onChange={(e) => setF({ ...f, carrosserie: e.target.value })} /></Champ>
            <Champ label="Devis mécanique (DZD)"><input type="number" value={f.mecanique} onChange={(e) => setF({ ...f, mecanique: e.target.value })} /></Champ>
            <Champ label="Main d'oeuvre (DZD)"><input type="number" value={f.mainOeuvre} onChange={(e) => setF({ ...f, mainOeuvre: e.target.value })} /></Champ>
            <Champ label="Jours d'immobilisation"><input type="number" value={f.jours} onChange={(e) => setF({ ...f, jours: e.target.value })} /></Champ>
            <Champ label="Tarif journalier (DZD)"><input type="number" value={f.tarifJour} onChange={(e) => setF({ ...f, tarifJour: e.target.value })} /></Champ>
            <Champ label="Prise en charge assurance (DZD)"><input type="number" value={f.assurance} onChange={(e) => setF({ ...f, assurance: e.target.value })} /></Champ>
          </div>
        </Carte>

        <Carte titre="Résultat" className="signature-kpi">
          {!calcul ? <Squelette lignes={3} /> : (
            <>
              <div className="micro">A la charge du locataire</div>
              <div className="kpi"><div className="v">{montant(calcul.partLocataireMinor)}</div></div>
              <div style={{ marginTop: 12 }}>
                <Bandeau type="info" titre={calcul.regleAppliquee}>{calcul.detailCalcul}</Bandeau>
              </div>
              <table className="tbl compact" style={{ marginTop: 12 }}>
                <tbody>
                  <tr><td>Réparation</td><td className="droite montant">{montant(calcul.base.reparation)}</td></tr>
                  <tr><td>Main d'oeuvre</td><td className="droite montant">{montant(calcul.base.mainOeuvre)}</td></tr>
                  <tr><td>Immobilisation</td><td className="droite montant">{montant(calcul.base.immobilisation)}</td></tr>
                </tbody>
              </table>
              {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
              {peut('modifier.finance') ? (
                <button className="btn btn-p btn-l btn-bloc" style={{ marginTop: 14 }} disabled={!f.vehicle} onClick={() => void enregistrer()}>
                  Enregistrer le sinistre
                </button>
              ) : (
                <p className="mini sourdine" style={{ marginTop: 12 }}>
                  Vous pouvez simuler autant que vous voulez. L'enregistrement d'un sinistre est une opération financière : il est réservé à Hichem.
                </p>
              )}
            </>
          )}
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Sinistres enregistrés" pad={false}>
          {liste.length === 0 ? <div className="carte-p"><Vide titre="Aucun sinistre" /></div> : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead><tr><th>Date</th><th>Véhicule</th><th>Règle appliquée</th><th className="droite">Part locataire</th><th className="droite">Caution déduite</th><th className="droite">Reste dû</th></tr></thead>
                <tbody>
                  {liste.map((s) => (
                    <tr key={s._id}>
                      <td className="num">{dateFr(s.date)}</td>
                      <td>{s.vehicle?.marque} {s.vehicle?.modele}<div className="mini sourdine">{s.vehicle?.immatriculation}</div></td>
                      <td className="mini sourdine">{s.detailCalcul.split('.')[0]}</td>
                      <td className="droite montant">{montant(s.partLocataireMinor)}</td>
                      <td className="droite montant">{montant(s.cautionDeduiteMinor)}</td>
                      <td className="droite montant">{montant(s.resteADevoirMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 800 }}>
        Facturer 60 % a un locataire non fautif est une clause commercialement agressive : c'est la vôtre, elle est
        appliquée telle quelle. C'est aussi celle qui génère le plus de litiges — l'état des lieux photographique
        du départ reste votre meilleure protection.
      </p>
    </>
  );
}
